import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";

export function Landing() {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 md:px-10 py-5 max-w-5xl mx-auto w-full flex justify-between items-center">
        <Link to="/"><Logo /></Link>
        {/* Auth flow must run on the app subdomain so the PKCE verifier in
            localStorage is on the same origin as the OAuth callback. */}
        <a href="https://app.thelivephotos.com/login" className="btn-primary text-sm">sign in</a>
      </header>

      <main className="flex-1 grid place-items-center px-6 pb-12">
        <div className="max-w-5xl w-full text-center animate-rise">
          <h1 className="font-display font-black text-5xl md:text-6xl leading-[0.95] mb-4">
            bring your photos<br /><em className="text-coral not-italic">to life</em>
          </h1>
          <p className="text-ink-soft text-lg mb-12 max-w-md mx-auto">
            drop a still photo, describe a moment, get a short video back.
          </p>

          <div className="relative pb-10 space-y-6 sm:space-y-10">
            <div className="sm:-translate-x-12 md:-translate-x-24 lg:-translate-x-32">
              <BeforeAfterHero
                before="/demos/golden-hour-before.jpg"
                after="/demos/golden-hour-after.mp4"
                label="golden hour"
                tilt="-rotate-2"
              />
            </div>
            <div className="relative z-10 sm:translate-x-12 md:translate-x-24 lg:translate-x-32">
              <BeforeAfterHero
                before="/demos/blurred-background-before.jpg"
                after="/demos/blurred-background-after.mp4"
                label="blurred background"
                tilt="rotate-2"
                reverse
              />
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <a href="https://app.thelivephotos.com/signup" className="btn-primary text-2xl px-10 py-4">lets get started!</a>
          </div>
        </div>
      </main>

      <footer className="text-center text-xs text-ink-mute py-10 flex justify-center gap-4 flex-wrap">
        <span className="opacity-70">© Terradynamic Ltd</span>
        <a href="/terms" className="hover:text-ink-soft">terms</a>
        <a href="/privacy" className="hover:text-ink-soft">privacy</a>
        <a href="/report" className="hover:text-ink-soft">report content</a>
      </footer>
    </div>
  );
}

function BeforeAfterHero({
  before, after, label, tilt = "-rotate-1", reverse = false,
}: { before: string; after: string; label: string; tilt?: string; reverse?: boolean }) {
  const beforePanel = (
    <Panel>
      <img src={before} alt="before" className="block w-full h-full object-cover" />
    </Panel>
  );
  const afterPanel = (
    <Panel>
      <video
        src={after}
        autoPlay muted loop playsInline preload="metadata" poster={before}
        className="block w-full h-full object-cover"
      />
    </Panel>
  );
  return (
    <div className={`bg-white shadow-polaroid rounded-polaroid p-4 pb-6 ${tilt} hover:rotate-0 transition-transform duration-500 max-w-3xl mx-auto`}>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-5">
        {reverse ? afterPanel : beforePanel}
        <SpiralArrow flipped={reverse} />
        {reverse ? beforePanel : afterPanel}
      </div>
      <p className={`font-display text-xl mt-4 px-1 ${reverse ? "text-right" : "text-left"}`}>{label}</p>
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative aspect-[3/4] rounded-sm overflow-hidden bg-ink shadow-soft">
      {children}
    </div>
  );
}

/** Big cartoon spiral arrow that swoops with a coral stroke and ink sticker shadow.
 *  When `flipped`, the arrow points right→left so reverse layouts still go before→after. */
function SpiralArrow({ flipped = false }: { flipped?: boolean }) {
  return (
    <svg
      className={`w-16 sm:w-24 md:w-28 -my-2 ${flipped ? "-scale-x-100" : ""}`}
      viewBox="0 0 220 140"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <g className="text-ink" strokeWidth="14" opacity="0.18">
        <SpiralPath />
      </g>
      <g className="text-coral" strokeWidth="9">
        <SpiralPath />
      </g>
    </svg>
  );
}

function SpiralPath() {
  return (
    <>
      {/* spiral that loops once before flying right into an arrowhead */}
      <path d="
        M 12 70
        C 30 30, 72 18, 92 50
        C 108 76, 60 96, 56 64
        C 54 36, 120 18, 150 64
        C 162 84, 180 84, 200 70
      " />
      <path d="M 200 70 L 178 56" />
      <path d="M 200 70 L 180 86" />
    </>
  );
}

